import React, { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Trash2, Search, Filter, Download } from "lucide-react";
import { TestLog } from "./TestingComponent";

interface TestLoggerProps {
  logs: TestLog[];
  onClearLogs: () => void;
}

export const TestLogger = forwardRef<any, TestLoggerProps>(({ logs, onClearLogs }, ref) => {
  const [filteredLogs, setFilteredLogs] = useState<TestLog[]>(logs);
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [autoScroll, setAutoScroll] = useState<boolean>(true);

  useImperativeHandle(ref, () => ({
    scrollToBottom: () => {
      if (autoScroll) {
        const scrollArea = document.querySelector('[data-testid="log-scroll-area"]');
        if (scrollArea) {
          scrollArea.scrollTop = scrollArea.scrollHeight;
        }
      }
    }
  }), [autoScroll]);

  useEffect(() => {
    let filtered = logs;

    // Filter by level
    if (levelFilter !== 'all') {
      filtered = filtered.filter(log => log.level === levelFilter);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (log.details && JSON.stringify(log.details).toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    setFilteredLogs(filtered);
  }, [logs, levelFilter, searchQuery]);

  const clearLogs = () => {
    onClearLogs();
  };

  const exportLogs = () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      totalLogs: logs.length,
      filteredLogs: filteredLogs.length,
      filters: { level: levelFilter, search: searchQuery },
      logs: filteredLogs,
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cali-test-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getLevelColor = (level: TestLog['level']) => {
    switch (level) {
      case 'error': return 'bg-destructive text-destructive-foreground';
      case 'warn': return 'bg-orange-500 text-white';
      case 'info': return 'bg-primary text-primary-foreground';
      case 'success': return 'bg-green-600 text-white';
      case 'debug': return 'bg-muted text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit'
    }) + '.' + date.getMilliseconds().toString().padStart(3, '0');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Test Execution Logs</CardTitle>
            <CardDescription>
              Real-time logging with filtering and search capabilities ({filteredLogs.length} of {logs.length} logs shown)
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportLogs} disabled={logs.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" size="sm" onClick={clearLogs} disabled={logs.length === 0}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={levelFilter} onValueChange={setLevelFilter}>
            <SelectTrigger className="w-32">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="error">Error</SelectItem>
              <SelectItem value="warn">Warning</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="debug">Debug</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Log display */}
        <ScrollArea 
          className="h-96 border rounded-lg p-4 bg-muted/50" 
          data-testid="log-scroll-area"
        >
          <div className="space-y-2 font-mono text-sm">
            {filteredLogs.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                {logs.length === 0 ? 'No logs yet. Run some tests to see output here.' : 'No logs match your filters.'}
              </div>
            ) : (
              filteredLogs.map((log) => (
                <div key={log.id} className="flex items-start gap-3 p-2 rounded hover:bg-muted/80">
                  <div className="flex-shrink-0">
                    <span className="text-xs text-muted-foreground">
                      {formatTimestamp(log.timestamp)}
                    </span>
                  </div>
                  <div className="flex-shrink-0">
                    <Badge className={`text-xs ${getLevelColor(log.level)}`}>
                      {log.level.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-foreground">{log.message}</div>
                    {log.details && (
                      <details className="mt-1">
                        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-primary">
                          Show details
                        </summary>
                        <pre className="mt-1 p-2 bg-background border rounded text-xs overflow-auto max-h-32">
                          {typeof log.details === 'string' 
                            ? log.details 
                            : JSON.stringify(log.details, null, 2)
                          }
                        </pre>
                      </details>
                    )}
                    {log.testId && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Test ID: {log.testId}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
});